'use client';

import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  format,
  isWeekend,
  startOfMonth,
} from 'date-fns';

import { frenchHolidays } from '@/features/timesheet/helpers/holidays';
import {
  ActivityType,
  OnCallDutyType,
  Timesheet,
  TimesheetCompletionSummary,
  TimesheetStatus,
} from '@/shared/types/timesheet';

const fmt = (d: Date) => format(d, 'yyyy-MM-dd');

/** Working days of a month: no weekends/holidays, bounded by contract */
function businessDaysOfMonth(
  monthRef: Date,
  startBound: Date,
  endBound?: Date,
  holidaySet?: Set<string>,
): Date[] {
  const start = startOfMonth(monthRef);
  const end = endOfMonth(monthRef);
  return eachDayOfInterval({ start, end }).filter(d => {
    if (isWeekend(d)) return false;
    if (holidaySet?.has(fmt(d))) return false;
    if (d < startBound) return false;
    if (endBound && d > endBound) return false;
    return true;
  });
}

/** Compute total minutes and cross-midnight flag from HH:mm -> HH:mm */
function computeOnCallMeta(startTime: string, endTime: string) {
  const [sh, sm] = startTime.split(':').map(Number);
  const [eh, em] = endTime.split(':').map(Number);
  const startMin = sh * 60 + sm;
  const endMin = eh * 60 + em;
  const crossMidnight = endMin < startMin;
  const minutes = crossMidnight ? 24 * 60 - startMin + endMin : endMin - startMin;
  return { minutes, crossMidnight };
}

/**
 * Generate consistent timesheets from contract start to today (or contract end).
 * Adds absences / non-billable variations and (NEW) on-call duties on some months.
 */
export function generateTimesheets(opts: {
  userId: string;
  contractStart: Date;
  contractEnd?: Date;
  projectName?: string;
  /** Mission rattachée à la ligne projet (lien CRA ↔ mission). */
  missionId?: string;
  includeNonBillable?: boolean;
  includeAlsaceMoselleHolidays?: boolean;
}): Timesheet[] {
  const {
    userId,
    contractStart,
    contractEnd,
    projectName = 'Optim.Net - CACIB',
    missionId,
    includeNonBillable = true,
    includeAlsaceMoselleHolidays = false,
  } = opts;

  const today = new Date();
  const hardEnd = contractEnd && contractEnd < today ? contractEnd : today;

  const out: Timesheet[] = [];
  let cursor = startOfMonth(contractStart);
  let seq = 1;

  while (cursor <= hardEnd) {
    const year = cursor.getFullYear();
    const month = cursor.getMonth() + 1;
    const isCurrent = year === today.getFullYear() && month === today.getMonth() + 1;

    const holidaySet = new Set(
      frenchHolidays(year, { includeAlsaceMoselle: includeAlsaceMoselleHolidays }),
    );

    const monthDays = businessDaysOfMonth(cursor, contractStart, contractEnd, holidaySet);
    const workingDays = monthDays.length;

    // --- Base project (1 day per working day) + ~20% remote at the start
    const projectDays: Record<string, number> = {};
    const remoteWorkDays: Record<string, boolean> = {};
    if (!isCurrent) {
      monthDays.forEach(d => (projectDays[fmt(d)] = 1));
      const ttCount = Math.max(0, Math.floor(workingDays * 0.2));
      monthDays.slice(0, ttCount).forEach(d => (remoteWorkDays[fmt(d)] = true));
    }

    // Utility to move a project day into another activity (no double booking)
    const moveFromProject = (d: Date, into: Record<string, number>, value = 1) => {
      const k = fmt(d);
      if (projectDays[k]) delete projectDays[k];
      if (remoteWorkDays[k]) delete remoteWorkDays[k];
      into[k] = value;
    };

    // Variations
    const sicknessDays: Record<string, number> = {};
    const birthLeaveDays: Record<string, number> = {};
    const nonBillableDays: Record<string, number> = {};

    // Past months only
    if (!isCurrent && workingDays > 0) {
      // Sickness: even months, 1 day (2 days if month % 4 === 0)
      if (month % 2 === 0) {
        const baseIdx = Math.min(workingDays - 1, 3);
        moveFromProject(monthDays[baseIdx], sicknessDays, 1);
        if (month % 4 === 0 && workingDays > baseIdx + 1) {
          moveFromProject(monthDays[baseIdx + 1], sicknessDays, 1);
        }
      }

      // Birth leave (3 consecutive days): February of odd years (example)
      if (month === 2 && year % 2 === 1 && workingDays >= 3) {
        const startIdx = Math.max(0, Math.floor(workingDays / 3));
        monthDays.slice(startIdx, startIdx + 3).forEach(d => moveFromProject(d, birthLeaveDays, 1));
      }

      // Non-billable day: months where (month % 3 === 1), pick a Friday if any
      if (includeNonBillable && month % 3 === 1) {
        const friday = monthDays.find(d => d.getDay() === 5);
        if (friday) moveFromProject(friday, nonBillableDays, 1);
      }
    }

    // Build activities (no overlapping thanks to moveFromProject)
    const activities = [
      {
        id: String(seq++),
        type: ActivityType.PROJECT,
        name: projectName,
        ...(missionId ? { missionId } : {}),
        daysWork: projectDays,
        remoteWorkDays,
      },
    ] as Timesheet['activities'];

    if (Object.keys(sicknessDays).length) {
      activities.push({
        id: String(seq++),
        type: ActivityType.ABSENCE_JUSTIFIED,
        name: 'Maladie',
        daysWork: sicknessDays,
        remoteWorkDays: {},
      });
    }
    if (Object.keys(birthLeaveDays).length) {
      activities.push({
        id: String(seq++),
        type: ActivityType.ABSENCE_TO_BE_JUSTIFIED,
        name: 'Naissance (à justifier)',
        daysWork: birthLeaveDays,
        remoteWorkDays: {},
      });
    }
    if (Object.keys(nonBillableDays).length) {
      activities.push({
        id: String(seq++),
        type: ActivityType.OTHER,
        name: 'Jour non facturé',
        daysWork: nonBillableDays,
        remoteWorkDays: {},
      });
    }

    // --- NEW: On-call duties (attach to the project activity)
    // Rule example: add on-call when month % 3 === 2 and it's not the current month.
    let onCallDuties: NonNullable<(typeof activities)[0]['onCallDuties']> | undefined;
    if (!isCurrent && workingDays >= 1 && month % 3 === 2) {
      // Pick safe indices inside working days
      const firstIdx = Math.min(workingDays - 1, 6); // ~first week
      const midIdx = Math.min(workingDays - 1, Math.floor(workingDays / 2)); // mid-month
      const d1 = monthDays[firstIdx];
      const d2 = monthDays[midIdx];

      // Same-day: 18:00 → 22:00
      const { minutes: m1, crossMidnight: c1 } = computeOnCallMeta('18:00', '22:00');
      // Cross-midnight: 22:00 → 02:00 (fin le lendemain)
      const { minutes: m2, crossMidnight: c2 } = computeOnCallMeta('22:00', '02:00');

      onCallDuties = [
        {
          id: `oc-${year}-${month}-1`,
          date: fmt(d1),
          startTime: '18:00',
          endTime: '22:00',
          crossMidnight: c1,
          minutes: m1,
          type: OnCallDutyType.HNO,
          comments: 'Astreinte de soirée',
        },
        {
          id: `oc-${year}-${month}-2`,
          date: fmt(d2),
          startTime: '22:00',
          endTime: '02:00',
          crossMidnight: c2,
          minutes: m2,
          type: OnCallDutyType.Astreinte,
          comments: 'Astreinte de nuit',
        },
      ];
    }

    if (onCallDuties?.length) {
      // Attach to the first (project) activity
      (activities[0] as any).onCallDuties = onCallDuties;
    }

    // Totals
    const totalWorkedDays =
      Object.values(projectDays).reduce((s, n) => s + n, 0) +
      Object.values(nonBillableDays).reduce((s, n) => s + n, 0);

    const totalAbsenceDays =
      Object.values(sicknessDays).reduce((s, n) => s + n, 0) +
      Object.values(birthLeaveDays).reduce((s, n) => s + n, 0);

    const totalRemoteWorkDays = Object.values(remoteWorkDays).filter(Boolean).length;

    // Status
    const timesheetStatus: TimesheetStatus = isCurrent
      ? TimesheetStatus.OPEN
      : month % 4 === 0
        ? TimesheetStatus.COMPLETED
        : month % 3 === 0
          ? TimesheetStatus.SUBMITTED
          : TimesheetStatus.VALIDATED;

    // hasOnCallDuty reflects whether we created entries
    const hasOnCallDuty = Boolean(onCallDuties?.length);

    out.push({
      id: String(out.length + 1),
      userId,
      year,
      month,
      timesheetStatus,
      workingDays,
      totalWorkedDays,
      totalRemoteWorkDays,
      totalAbsenceDays,
      hasOnCallDuty,
      activities,
      comment: isCurrent ? '' : 'Généré automatiquement (inclut TT/absences/JA).',
    });

    cursor = addMonths(cursor, 1);
  }

  return out;
}

// Hash simple pour un ratio stable par user/mois
function seededFillRatio(seed: string): number {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  // ratio entre 0.35 et 0.92 (évite les 0% et 100% accidentels)
  return 0.35 + (h % 5700) / 10000;
}

// Clamp pourcentages
const clamp = (n: number, a: number, b: number) => Math.max(a, Math.min(b, n));

// ======= Mock principal =======
export async function fetchTimesheetCompletionMock(
  userId: string,
  now = new Date(),
): Promise<TimesheetCompletionSummary> {
  // Simule une latence réaliste
  await new Promise(r => setTimeout(r, 250));

  const cy = now.getFullYear();
  const cm = now.getMonth() + 1; // 1..12

  // Mois précédent (gère changement d’année)
  const py = cm === 1 ? cy - 1 : cy;
  const pm = cm === 1 ? 12 : cm - 1;

  // --- Mois précédent : toujours COMPLETED (100%) ---
  const prevItem = {
    year: py,
    month: pm,
    percent: 100,
    status: 'COMPLETED' as const,
  };

  // ratio stable par user/mois courant
  const ratioCur = seededFillRatio(`${userId}:${cy}-${cm}:cur`);
  // on borne le pourcentage dans [20..95] pour être crédible et éviter 0/100
  const percentCur = clamp(Math.round(ratioCur * 100), 20, 95);

  const curItem = {
    year: cy,
    month: cm,
    percent: percentCur,
    status: 'IN_PROGRESS' as const,
  };

  return {
    userId,
    generatedAt: new Date().toISOString(),
    items: [prevItem, curItem],
  };
}
